if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('keyup', event => {
        if (event.code === 'Escape') this.close();
      });
      this.addEventListener('click', event => {
        if (event.target.closest('#CartDrawer-Overlay')) this.close();
      });
      this.headerClick = event => {
        const button = event.target.closest('#cart-icon-bubble');
        if (!button) return;
        event.preventDefault();
        this.open(button);
      };
      this.headerKeydown = event => {
        if (event.code !== 'Space' || !event.target.closest('#cart-icon-bubble')) return;
        event.preventDefault();
        this.open(event.target.closest('#cart-icon-bubble'));
      };
    }

    connectedCallback() {
      this.setHeaderCartIconAccessibility();
      document.addEventListener('click', this.headerClick);
      document.addEventListener('keydown', this.headerKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.headerClick);
      document.removeEventListener('keydown', this.headerKeydown);
      cancelAnimationFrame(this.openFrame);
    }

    setHeaderCartIconAccessibility() {
      const button = document.getElementById('cart-icon-bubble');
      if (!button) return;
      button.setAttribute('role', 'button');
      button.setAttribute('aria-haspopup', 'dialog');
    }

    open(element) {
      if (element) this.setActiveElement(element);
      const summary = this.querySelector('[id^="Details-"] summary');
      if (summary && !summary.hasAttribute('role')) this.setSummaryAccessibility(summary);
      cancelAnimationFrame(this.openFrame);
      this.openFrame = requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.classList.add('animate', 'active');
        document.body.classList.add('overflow-hidden');
        const items = this.querySelector('cart-drawer-items');
        items?.updateCartUpsellToggleState?.();
        const count = Array.from(this.querySelectorAll('input[name="updates[]"]'))
          .reduce((total, input) => total + (Number(input.value) || 0), 0);
        items?.updateCartUpsellVisibility?.(count);
        const inner = this.querySelector('.drawer__inner');
        if (inner && typeof trapFocus === 'function') trapFocus(inner, inner);
      });
    }

    close() {
      cancelAnimationFrame(this.openFrame);
      this.classList.remove('active');
      const trigger = this.activeElement?.isConnected && this.activeElement.getClientRects().length
        ? this.activeElement : document.getElementById('cart-icon-bubble');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(trigger);
      document.body.classList.remove('overflow-hidden');
    }

    setSummaryAccessibility(summary) {
      summary.setAttribute('role', 'button');
      summary.setAttribute('aria-expanded', String(summary.parentElement.hasAttribute('open')));
      if (summary.nextElementSibling?.id) summary.setAttribute('aria-controls', summary.nextElementSibling.id);
      summary.addEventListener('click', () => {
        summary.setAttribute('aria-expanded', String(!summary.closest('details').hasAttribute('open')));
      });
      if (typeof onKeyUpEscape === 'function') summary.parentElement.addEventListener('keyup', onKeyUpEscape);
    }

    cartURL() {
      const url = new URL(window.routes.cart_url, window.location.origin);
      const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
      const theme = window.Shopify?.theme;
      if (preview) url.searchParams.set('preview_theme_id', preview);
      else if (theme?.id && theme.role && theme.role !== 'main') url.searchParams.set('preview_theme_id', theme.id);
      return url;
    }

    async read(url, json = false) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetch(url, {
          credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
          headers: { Accept: json ? 'application/json' : 'text/html' }
        });
        if (!response.ok) throw new Error(window.cartStrings.error);
        return await (json ? response.json() : response.text());
      } finally { clearTimeout(timer); }
    }

    parseSections(sections) {
      const html = sections?.['cart-drawer'];
      if (typeof html !== 'string') return null;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const drawer = doc.querySelector('#CartDrawer');
      if (!drawer?.querySelector('.drawer__inner') || !drawer.querySelector('cart-drawer-items')) return null;
      const iconHTML = sections?.['cart-icon-bubble'];
      const icon = typeof iconHTML === 'string'
        ? new DOMParser().parseFromString(iconHTML, 'text/html').querySelector('.shopify-section') : null;
      return { drawer, icon, empty: !!drawer.querySelector('cart-drawer-items.is-empty') };
    }

    async freshSections() {
      const url = this.cartURL();
      url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
      try {
        const sections = await this.read(url, true);
        if (this.parseSections(sections)) return sections;
      } catch (_) {}
      const html = await this.read(this.cartURL());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const drawer = doc.querySelector('#CartDrawer');
      const icon = doc.getElementById('cart-icon-bubble');
      return {
        'cart-drawer': drawer?.outerHTML,
        'cart-icon-bubble': icon ? '<div class="shopify-section">' + icon.innerHTML + '</div>' : null
      };
    }

    async renderContents(result = {}, open = true) {
      try {
        let parsed = this.parseSections(result.sections);
        if (!parsed || !parsed.icon) {
          try {
            const fresh = await this.freshSections();
            const replacement = this.parseSections(fresh);
            if (replacement) { result.sections = fresh; parsed = replacement; }
          } catch (_) { if (!parsed) throw new Error(window.cartStrings.error); }
        }
        const target = this.querySelector('#CartDrawer');
        if (!target || !parsed) throw new Error(window.cartStrings.error);
        target.innerHTML = parsed.drawer.innerHTML;
        this.productId = result.id;
        this.classList.toggle('is-empty', parsed.empty);
        const icon = document.getElementById('cart-icon-bubble');
        if (icon && parsed.icon) icon.innerHTML = parsed.icon.innerHTML;
        else if (icon) {
          const count = Array.from(target.querySelectorAll('input[name="updates[]"]'))
            .reduce((total, input) => total + (Number(input.value) || 0), 0);
          let bubble = icon.querySelector('.cart-count-bubble');
          if (!count) bubble?.remove();
          else {
            if (!bubble) { bubble = document.createElement('div'); bubble.className = 'cart-count-bubble'; icon.append(bubble); }
            bubble.textContent = String(count);
          }
        }
        if (open) this.open();
        return true;
      } catch (_) {
        const inner = this.querySelector('.drawer__inner');
        if (inner && !inner.querySelector('[data-cart-refresh-error]')) {
          const message = document.createElement('p');
          message.setAttribute('data-cart-refresh-error', '');
          message.setAttribute('role', 'alert');
          message.textContent = 'Le panier n’a pas pu être actualisé. ';
          const link = document.createElement('a');
          link.href = this.cartURL().href;
          link.textContent = 'Afficher mon panier';
          message.append(link);
          inner.prepend(message);
          const checkout = inner.querySelector('#CartDrawer-Checkout');
          if (checkout) checkout.disabled = true;
        }
        if (open) this.open();
        return false;
      }
    }

    async refresh(open = true) {
      return this.renderContents({}, open);
    }

    addItem(formData, options = {}) {
      const operation = async () => {
        this.setActiveElement(options.button || document.activeElement);
        if (options.prepare) await options.prepare();
        formData.set('sections', this.getSectionsToRender().map(section => section.id).join(','));
        const contextURL = this.cartURL();
        formData.set('sections_url', contextURL.pathname + contextURL.search);
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 20000);
        let result;
        try {
          const url = window.routes.cart_add_url.replace(/\.js$/, '') + '.js';
          const response = await fetch(url, {
            method: 'POST', credentials: 'same-origin', signal: controller.signal,
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData
          });
          result = await response.json();
          if (!response.ok || (result.status && Number(result.status) !== 200)) {
            const error = new Error(result.description || window.cartStrings.error);
            error.cartRejected = true;
            throw error;
          }
          if (!result.id && !result.items?.length) throw new Error(window.cartStrings.error);
        } catch (error) {
          if (!error.cartRejected) {
            await this.refresh();
            error.message = 'La confirmation de l’ajout n’a pas été reçue. Vérifiez le panier avant de réessayer.';
          }
          throw error;
        } finally { clearTimeout(timer); }
        if (options.beforeRender) await options.beforeRender();
        const rendered = options.skipCart ? false : await this.renderContents(result);
        try {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: options.source || 'product-card', productVariantId: formData.get('id'),
            cartData: result, cartRendered: rendered
          });
        } catch (_) {}
        return result;
      };
      const pending = (this.pendingAdd || Promise.resolve()).then(operation, operation);
      this.pendingAdd = pending.catch(() => {});
      return pending;
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      return this.getSectionDOM(html, selector)?.innerHTML || '';
    }
    getSectionsToRender() { return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }]; }
    getSectionDOM(html, selector = '.shopify-section') {
      return typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html').querySelector(selector) : null;
    }
    setActiveElement(element) { this.activeElement = element; }
  }
  customElements.define('cart-drawer', CartDrawer);
}

if (!customElements.get('cart-drawer-items')) {
  class CartDrawerItems extends CartItems {
    connectedCallback() {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
        if (event.source === 'cart-items' || event.cartRendered) return;
        if (['product-card', 'product-form'].includes(event.source) && event.cartData?.sections?.['cart-drawer']) return;
        this.onCartUpdate();
      });
    }
    onCartUpdate() { return this.closest('cart-drawer')?.refresh(false); }
    getSectionsToRender() {
      return [{ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }];
    }
  }
  customElements.define('cart-drawer-items', CartDrawerItems);
}
